// ============================================================
// Stocora — Settings › Team (admin invite, 2026-07-19)
// ============================================================
// Replaces the fully-manual onboarding workflow (Supabase dashboard
// "Add user" + a hand-run SQL snippet) with an in-app surface. This
// was the long-standing "Admin interface for clinic + user
// onboarding" open item from the 2026-04-24 auth-model decision,
// finally picked up as part of the password-auth switch.
//
// Visibility split (mirrors VendorsSettingsPage's convention — see
// its header comment for the rationale): the ROSTER is visible to
// every clinic member (RLS: clinic_members_select is any_member).
// The invite form, role dropdowns, and remove buttons are
// owner-only — invite goes through invite-clinic-member (gated by
// clinic.member.invite), role-change/remove go through direct
// clinic_members writes (RLS: owner-only on insert/update/delete,
// 20260423194552 — no has_permission() entry needed for those, see
// the migration header for why).
//
// Last-owner guard: CLIENT-SIDE ONLY. Disables self-demotion and
// self-removal when the fetched roster shows exactly one owner and
// that owner is you. This is a UX safety net, not a hard invariant
// — there's no DB constraint stopping an owner from being removed
// via direct SQL, and a second owner could still remove the last
// other owner in a race (two browser tabs). Flagged as a known gap
// in the migration ticket rather than solved here; a DB trigger
// enforcing "at least one owner per clinic" is the correct fix if
// this ever bites in practice, and is deferred as low-probability
// for pilot scale.
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useSession, type ClinicRole } from '@/lib/session';
import Icon from '@/components/Icon';

interface MemberRow {
  readonly id: string; // clinic_members.id
  readonly user_id: string;
  readonly role: ClinicRole;
  readonly created_at: string;
  readonly full_name: string | null;
  readonly email_cached: string | null;
}

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; members: readonly MemberRow[] };

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROLE_LABELS: Record<ClinicRole, string> = {
  owner: 'Owner',
  manager: 'Manager',
  staff: 'Staff',
};

export default function TeamSettingsPage() {
  const session = useSession();
  const clinicId = session.activeMembership.clinic_id;
  const effectiveRole = session.viewAsRole ?? session.activeMembership.role;
  const canManage = effectiveRole === 'owner';

  const [state, setState] = useState<FetchState>({ kind: 'loading' });
  const [refetchToken, setRefetchToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    (async () => {
      const { data: memberRows, error: memberErr } = await supabase
        .from('clinic_members')
        .select('id, user_id, role, created_at')
        .eq('clinic_id', clinicId)
        .order('created_at', { ascending: true });

      if (cancelled) return;

      if (memberErr) {
        console.error('[stocora] Team members fetch failed:', memberErr);
        setState({ kind: 'error', message: memberErr.message });
        return;
      }

      const rows = memberRows ?? [];
      const userIds = rows.map((r) => r.user_id);

      // No direct FK between clinic_members and public.users (both
      // reference auth.users independently, but not each other), so
      // PostgREST can't resolve a nested embed here — two queries,
      // merged client-side. RLS on public.users (users_select_self_or_
      // clinicmate) already permits reading any clinicmate's row.
      const { data: userRows, error: userErr } =
        userIds.length > 0
          ? await supabase.from('users').select('id, full_name, email_cached').in('id', userIds)
          : { data: [], error: null };

      if (cancelled) return;

      if (userErr) {
        console.error('[stocora] Team member profiles fetch failed:', userErr);
        setState({ kind: 'error', message: userErr.message });
        return;
      }

      const usersById = new Map((userRows ?? []).map((u) => [u.id, u]));

      const members: MemberRow[] = rows.map((row) => {
        const profile = usersById.get(row.user_id);
        return {
          id: row.id,
          user_id: row.user_id,
          role: row.role as ClinicRole,
          created_at: row.created_at,
          full_name: profile?.full_name ?? null,
          email_cached: profile?.email_cached ?? null,
        };
      });

      setState({ kind: 'loaded', members });
    })();

    return () => {
      cancelled = true;
    };
  }, [clinicId, refetchToken]);

  const handleChanged = useCallback(() => {
    setRefetchToken((n) => n + 1);
  }, []);

  const owners =
    state.kind === 'loaded' ? state.members.filter((m) => m.role === 'owner') : [];
  const isOnlyOwner = owners.length === 1 && owners[0]?.user_id === session.userId;

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="text-slate-400 hover:text-slate-700 transition"
          aria-label="Back to settings"
        >
          <Icon name="chevright" className="w-5 h-5 rotate-180" strokeWidth={2} />
        </button>
        <h1 className="text-base font-semibold text-slate-900">Team</h1>
      </div>

      <p className="text-[12px] text-slate-500 leading-snug">
        Everyone with access to your clinic.
        {!canManage && (
          <span className="block mt-1 text-slate-400">
            Only owners can invite teammates or change roles.
          </span>
        )}
      </p>

      {canManage && <InviteForm clinicId={clinicId} onInvited={handleChanged} />}

      {state.kind === 'loading' && (
        <div className="py-10 flex justify-center" aria-label="Loading team">
          <div className="w-6 h-6 border-[3px] border-slate-200 border-t-action rounded-full animate-spin" />
        </div>
      )}

      {state.kind === 'error' && (
        <div className="rounded-lg bg-rose-50 border border-rose-100 p-3 text-[13px] text-rose-800">
          Couldn&apos;t load team: {state.message}
        </div>
      )}

      {state.kind === 'loaded' && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
          {state.members.map((member) => (
            <MemberRowItem
              key={member.id}
              member={member}
              clinicId={clinicId}
              canManage={canManage}
              isSelf={member.user_id === session.userId}
              // A role-change/removal control gets disabled specifically
              // when acting on the one-and-only owner (whether or not
              // that owner is "you" in this session) — losing the last
              // owner locks the clinic out of admin actions for everyone,
              // not just the person clicking.
              isLastOwner={member.role === 'owner' && owners.length === 1}
              onChanged={handleChanged}
            />
          ))}
        </div>
      )}

      {state.kind === 'loaded' && isOnlyOwner && (
        <p className="text-[11px] text-slate-400 px-1">
          You&apos;re the only owner. Promote a teammate before stepping back from this role.
        </p>
      )}
    </div>
  );
}

// ---- Invite form --------------------------------------------------

type InviteState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'error'; message: string }
  | { kind: 'success'; message: string };

function InviteForm({
  clinicId,
  onInvited,
}: {
  clinicId: string;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ClinicRole>('staff');
  const [state, setState] = useState<InviteState>({ kind: 'idle' });

  const emailValid = EMAIL_PATTERN.test(email.trim());
  const canSubmit = emailValid && state.kind !== 'sending';

  const handleInvite = useCallback(async () => {
    setState({ kind: 'sending' });

    const {
      data: { session: authSession },
    } = await supabase.auth.getSession();
    if (!authSession) {
      setState({ kind: 'error', message: 'Your session expired — refresh and try again.' });
      return;
    }

    const { data, error } = await supabase.functions.invoke<{
      ok: boolean;
      status?: 'invited' | 'attached_existing_user' | 'already_member';
      error?: string;
    }>('invite-clinic-member', {
      body: { email: email.trim().toLowerCase(), clinic_id: clinicId, role },
    });

    if (error || !data?.ok) {
      setState({ kind: 'error', message: describeInviteError(data?.error ?? error?.message) });
      return;
    }

    const successMessage =
      data.status === 'attached_existing_user'
        ? `${email.trim()} already had a Stocora account — added to your team.`
        : data.status === 'already_member'
          ? `${email.trim()} is already on your team.`
          : `Invite sent to ${email.trim()}.`;

    setState({ kind: 'success', message: successMessage });
    setEmail('');
    setRole('staff');
    onInvited();
  }, [email, role, clinicId, onInvited]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 space-y-3">
      <div className="text-[13px] font-semibold text-slate-900">Invite a teammate</div>

      <div className="flex gap-2">
        <input
          type="email"
          inputMode="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder="teammate@clinic.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (state.kind !== 'idle') setState({ kind: 'idle' });
          }}
          disabled={state.kind === 'sending'}
          className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-action/20 focus:border-action transition bg-white disabled:bg-slate-50"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as ClinicRole)}
          disabled={state.kind === 'sending'}
          className="h-10 px-2.5 rounded-lg border border-slate-200 text-[14px] text-slate-900 bg-white disabled:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-action/20 focus:border-action transition"
        >
          <option value="staff">Staff</option>
          <option value="manager">Manager</option>
          <option value="owner">Owner</option>
        </select>
      </div>

      {state.kind === 'error' && (
        <p className="text-xs text-red-600">{state.message}</p>
      )}
      {state.kind === 'success' && (
        <p className="text-xs text-success">{state.message}</p>
      )}

      <button
        type="button"
        onClick={() => void handleInvite()}
        disabled={!canSubmit}
        className="w-full h-10 rounded-lg bg-action hover:bg-action-hover active:bg-action-active disabled:opacity-50 transition text-sm font-semibold text-white"
      >
        {state.kind === 'sending' ? 'Sending…' : 'Send invite'}
      </button>
    </div>
  );
}

function describeInviteError(raw: string | undefined): string {
  switch (raw) {
    case 'invalid_email':
      return 'That email address doesn\u2019t look right.';
    case 'invalid_role':
      return 'Pick a role before sending the invite.';
    case 'insufficient_permission':
      return 'Only owners can invite teammates.';
    case 'invite_failed':
      return 'Couldn\u2019t send the invite email. Try again in a moment.';
    default:
      return raw ?? 'Couldn\u2019t send the invite. Try again.';
  }
}

// ---- Per-member row -------------------------------------------------

type RowActionState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'error'; message: string };

function MemberRowItem({
  member,
  clinicId,
  canManage,
  isSelf,
  isLastOwner,
  onChanged,
}: {
  member: MemberRow;
  clinicId: string;
  canManage: boolean;
  isSelf: boolean;
  isLastOwner: boolean;
  onChanged: () => void;
}) {
  const [action, setAction] = useState<RowActionState>({ kind: 'idle' });
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const handleRoleChange = useCallback(
    async (nextRole: ClinicRole) => {
      if (nextRole === member.role) return;
      setAction({ kind: 'saving' });

      const { error } = await supabase
        .from('clinic_members')
        .update({ role: nextRole })
        .eq('id', member.id)
        .eq('clinic_id', clinicId);

      if (error) {
        console.error('[stocora] Role change failed:', error);
        setAction({
          kind: 'error',
          message: error.code === '42501' ? 'Not allowed' : 'Couldn\u2019t save',
        });
        return;
      }

      setAction({ kind: 'idle' });
      onChanged();
    },
    [member.id, member.role, clinicId, onChanged],
  );

  const handleRemove = useCallback(async () => {
    setAction({ kind: 'saving' });

    const { error } = await supabase
      .from('clinic_members')
      .delete()
      .eq('id', member.id)
      .eq('clinic_id', clinicId);

    if (error) {
      console.error('[stocora] Member removal failed:', error);
      setAction({
        kind: 'error',
        message: error.code === '42501' ? 'Not allowed' : 'Couldn\u2019t remove',
      });
      setConfirmingRemove(false);
      return;
    }

    setAction({ kind: 'idle' });
    setConfirmingRemove(false);
    onChanged();
  }, [member.id, clinicId, onChanged]);

  const displayName = member.full_name ?? member.email_cached ?? 'Unknown';
  const disableDestructive = isLastOwner; // last owner can't be demoted or removed by anyone

  return (
    <div className="px-3.5 py-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <Icon name="user" className="w-4.5 h-4.5 text-slate-600" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium text-slate-900 truncate">
            {displayName}
            {isSelf && <span className="text-slate-400 font-normal"> (you)</span>}
          </div>
          {member.email_cached && member.full_name && (
            <div className="text-[12px] text-slate-500 truncate">{member.email_cached}</div>
          )}
        </div>

        {canManage ? (
          <select
            value={member.role}
            onChange={(e) => void handleRoleChange(e.target.value as ClinicRole)}
            disabled={action.kind === 'saving' || disableDestructive}
            className="h-8 px-2 rounded-lg border border-slate-200 text-[12px] text-slate-900 bg-white disabled:bg-slate-50 disabled:text-slate-400 focus:outline-none focus:ring-2 focus:ring-action/20 transition"
          >
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="owner">Owner</option>
          </select>
        ) : (
          <span className="text-[12px] font-medium text-slate-500 shrink-0">
            {ROLE_LABELS[member.role]}
          </span>
        )}
      </div>

      {action.kind === 'error' && (
        <p className="text-[11px] text-red-600 mt-1.5 pl-12">{action.message}</p>
      )}

      {canManage && (
        <div className="pl-12 mt-1.5">
          {confirmingRemove ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-500">Remove from clinic?</span>
              <button
                type="button"
                onClick={() => void handleRemove()}
                disabled={action.kind === 'saving'}
                className="text-[11px] font-medium text-red-600 hover:text-red-700"
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRemove(false)}
                className="text-[11px] font-medium text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingRemove(true)}
              disabled={disableDestructive}
              className="text-[11px] font-medium text-slate-400 hover:text-red-600 disabled:hover:text-slate-400 disabled:opacity-50 transition"
            >
              {disableDestructive ? 'Can\u2019t remove the only owner' : 'Remove'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
